import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import { Button, ButtonGroup, CircularProgress, InputAdornment, TextField } from '@material-ui/core';
import { Clear, Done } from '@material-ui/icons';
import { useDataSourceProps } from '../hooks/form/useDataSourceProps';
import { useFormContext } from '../FormContext';
export const FormNumberField = ({ element, edit, disabled, disableInlineSave }) => {
    const { formState, formValues, setFormValue } = useFormContext();
    const config = element.config || {};
    let { format, isString, prefix, thousandSeparator } = config;
    const { placeholder, suffix, phoneNumber, creditcard, allowLeadingZero, mask, length, min, max, decimalScale, currency, dataType } = config;
    const key = element.dataSource || element.key;
    const [value, setValue] = useState(formValues[key] || undefined);
    const [focus, setFocus] = useState(false);
    const [touched, setTouched] = useState(false);
    const previousValueRef = useRef(value);
    const cancelButtonRef = useRef();
    const dataSourceProps = useDataSourceProps(element, value);
    if (creditcard) {
        format = '#### #### #### ####';
    }
    else if (phoneNumber) {
        format = '+1 (###) ###-####';
        isString = true;
    }
    else if (currency) {
        prefix = '$';
        thousandSeparator = true;
    }
    else if (!format && length) {
        format = [...Array(length + 1)].join('#');
    }
    // refresh value if formValues change outside of the component
    useEffect(() => {
        if (previousValueRef.current !== formValues[key]) {
            setValue(formValues[key]);
            previousValueRef.current = formValues[key];
        }
    }, [formValues, key]);
    const handleSubmit = useCallback((event) => {
        if (event.isPropagationStopped() || event.isDefaultPrevented()) {
            return;
        }
        setFormValue(key, value);
    }, [key, value, setFormValue]);
    const handleCancel = () => {
        setValue(previousValueRef.current);
    };
    const inputProps = useMemo(() => {
        if (formState.isSubmitting && !disableInlineSave) {
            return {
                endAdornment: (React.createElement(InputAdornment, { position: 'end' },
                    React.createElement(CircularProgress, { color: 'primary', size: 25 })))
            };
        }
        else if (focus && !disableInlineSave) {
            return {
                endAdornment: (React.createElement(InputAdornment, { position: 'end' },
                    React.createElement(ButtonGroup, { color: 'primary', variant: 'text' },
                        React.createElement(Button, { size: 'small', tabIndex: -1, onClick: handleSubmit },
                            React.createElement(Done, null)),
                        React.createElement(Button, { size: 'small', tabIndex: -1, innerRef: cancelButtonRef, onClick: event => {
                                event.stopPropagation();
                                handleCancel();
                            } },
                            React.createElement(Clear, null)))))
            };
        }
    }, [disableInlineSave, focus, formState.isSubmitting, handleSubmit]);
    return (React.createElement(NumberFormat, { fullWidth: true, type: 'tel' // force number input on mobile
        , disabled: edit || disabled, variant: 'outlined', value: value, placeholder: placeholder, mask: mask, error: dataSourceProps.error, helperText: dataSourceProps.helperText, customInput: TextField, InputProps: inputProps, onFocus: () => setFocus(true), format: format, prefix: prefix, suffix: suffix, decimalScale: decimalScale, thousandSeparator: thousandSeparator, allowLeadingZeros: allowLeadingZero, onValueChange: values => {
            setTouched(true);
            // use values.value if dataType is string
            let value = values.floatValue !== undefined && dataType !== 'string'
                ? values.floatValue
                : values.value
                    ? values.value
                    : null;
            // validate value based on config min and max
            if (value !== null) {
                if (typeof max !== 'undefined' && value > max)
                    value = max;
                if (typeof min !== 'undefined' && value < min)
                    value = min;
            }
            if (isString) {
                // avoid "null" string
                value = value === null ? '' : `${value}`;
            }
            // console.log('FormAnswerNumberField value:', value, typeof value);
            setValue(value);
        }, onKeyDown: event => {
            switch (event.key) {
                case 'Escape':
                    return handleCancel();
            }
        }, onBlur: event => {
            setFocus(false);
            if (event.relatedTarget === cancelButtonRef.current) {
                handleCancel();
            }
            else {
                if (touched && formValues[key] !== value) {
                    handleSubmit(event);
                }
            }
        } }));
};
export default FormNumberField;
//# sourceMappingURL=FormNumberField.js.map